export const articles = [
  {
    id: 'se-ji-shi-kong-shen-ceng-han-yi',
    title: '「色即是空」的深层含义——超越字面的般若智慧',
    slug: 'se-ji-shi-kong-shen-ceng-han-yi',
    category: 'xin-jing-jie-du',
    categoryName: '心经解读',
    excerpt: '「色即是空」四个字看似简单，却蕴含了佛教最深邃的空性智慧。本文从字面到义理，层层深入解读这一《心经》核心偈语的真实内涵。',
    content: `
      <h2>一、「色即是空」的字面解读</h2>
      <p>「色即是空，空即是色」出自《般若波罗蜜多心经》，是大乘佛教最广为人知的偈语之一。在佛教术语中，「色」并非颜色，而是指代一切物质现象——凡是能够被感官所认知的有形之物，都属于「色」的范畴。</p>
      <p>「空」也并非虚无。许多人对「空」的理解存在偏差，以为空就是什么都没有。事实上，佛教所说的「空」是指万物没有固定不变的自性，一切法都是因缘和合而生，因缘消散而灭。</p>
      <h2>二、缘起性空——理解的关键</h2>
      <p>要理解「色即是空」，必须先理解「缘起」的概念。世间万物都不是独立存在的，而是由各种条件（因缘）聚合而成。一张桌子，需要木材、工匠、工具、时间等无数条件聚合才能产生。当这些条件变化，桌子也会随之变化、朽坏、消失。</p>
      <p>正因为一切法都是缘起的，所以它们没有永恒不变的本质——这就是「性空」。色法（物质现象）的本质就是空性，所以说「色即是空」。</p>
      <h2>三、「空即是色」——空性与现象的圆融</h2>
      <p>如果说「色即是空」揭示了物质现象的空性本质，那么「空即是色」则告诉我们：空性并不是脱离现象而独立存在的实体。空性就体现在千差万别的现象之中。</p>
      <p>正如龙树菩萨在《中论》中所说：「以有空义故，一切法得成。若无空义者，一切则不成。」正是因为万物本性是空，变化才成为可能，万象才得以呈现。</p>
      <h2>四、修行启示——从理解到体证</h2>
      <p>理解「色即是空」的意义不在于文字上的知晓，而在于将这份智慧融入生活的每一个当下。当我们面对顺境时不贪著，面对逆境时不嗔恨，因为知道一切都在变化之中，都是因缘的暂时聚合。</p>
      <p>这正是般若智慧的妙用——不是让我们消极避世，而是让我们以更清醒、更自在的心态去面对人生的种种境遇。</p>
    `,
    image: 'https://readdy.ai/api/search-image?query=Zen%20Buddhist%20temple%20interior%20with%20soft%20warm%20candlelight%2C%20incense%20smoke%20drifting%20gently%2C%20ancient%20wooden%20architecture%2C%20serene%20meditative%20atmosphere%2C%20minimalist%20aesthetic%2C%20warm%20golden%20and%20earthy%20tones%2C%20spiritual%20stillness%2C%20fine%20art%20photography%20style&width=800&height=450&seq=article-kong-01&orientation=landscape',
    date: '2024-12-15',
    author: '释明心',
    readTime: '12分钟',
    tags: ['色即是空', '心经', '般若智慧', '空性'],
  },
  {
    id: 'xin-jing-quan-wen-zhu-ju-jie-du',
    title: '《心经》全文逐句解读——二百六十字的智慧精华',
    slug: 'xin-jing-quan-wen-zhu-ju-jie-du',
    category: 'xin-jing-jie-du',
    categoryName: '心经解读',
    excerpt: '《心经》全文仅二百六十字，却浓缩了般若经的核心思想。本文逐句解析心经全文，带你领略这部佛经瑰宝的每一个字背后的深意。',
    content: `
      <h2>心经概述</h2>
      <p>《般若波罗蜜多心经》，简称《心经》，是佛教般若部经典中篇幅最短却义理最深奥的一部。全文仅二百六十字，由唐玄奘法师翻译，流传最为广泛。</p>
      <p>「心」字有两层含义：一是指核心、精华；二是指心要、心髓。《心经》将六百卷《大般若经》的精义浓缩于此，堪称般若智慧的结晶。</p>
      <h2>逐句解读</h2>
      <h3>观自在菩萨，行深般若波罗蜜多时，照见五蕴皆空，度一切苦厄。</h3>
      <p>观自在菩萨即观世音菩萨。当这位大菩萨深入修行般若智慧时，以智慧观照到色、受、想、行、识五蕴的本性都是空的，从而超越了一切痛苦和灾难。</p>
      <h3>舍利子，色不异空，空不异色，色即是空，空即是色，受想行识，亦复如是。</h3>
      <p>佛对舍利弗尊者说：物质现象与空性不是两个不同的东西，空性与物质现象也不是分离的。物质现象的本性就是空，空性就体现在物质现象中。感受、思想、意志、认识也是如此。</p>
      <h3>舍利子，是诸法空相，不生不灭，不垢不净，不增不减。</h3>
      <p>一切法的空性本质是不生不灭、不垢不净、不增不减的。这不是说现象层面没有生灭变化，而是说从空性的究竟层面来看，这些对立的观念都不成立。</p>
    `,
    image: 'https://readdy.ai/api/search-image?query=Ancient%20Buddhist%20sutra%20scroll%20on%20wooden%20desk%2C%20soft%20natural%20light%20from%20paper%20window%2C%20calligraphy%20brush%20and%20ink%20stone%2C%20traditional%20Chinese%20study%20room%2C%20serene%20scholarly%20atmosphere%2C%20warm%20earthy%20colors%2C%20meditative%20mood&width=800&height=450&seq=article-xinjing-02&orientation=landscape',
    date: '2024-11-28',
    author: '释明心',
    readTime: '18分钟',
    tags: ['心经全文', '逐句解读', '般若波罗蜜多', '玄奘'],
  },
  {
    id: 'ri-chang-sheng-huo-kong-xing-xiu-xing',
    title: '日常生活中的空性修行——把般若智慧活出来',
    slug: 'ri-chang-sheng-huo-kong-xing-xiu-xing',
    category: 'xiu-xing-shi-jian',
    categoryName: '修行实践',
    excerpt: '修行不只在禅堂，更在日常的一言一行中。本文分享如何将「色即是空」的智慧转化为生活中的实际修行方法。',
    content: `
      <h2>修行不在深山，在日常</h2>
      <p>很多人以为修行就是远离尘世，到深山古刹中打坐冥想。然而真正的修行恰恰在日常生活的每一个当下。在堵车时的焦躁中，在工作压力下的烦闷中，在人际关系的不如意中——这些恰恰是最好的修行道场。</p>
      <h2>观照情绪——情绪的「空性」</h2>
      <p>当你感到愤怒时，试着停下来观照这份愤怒。它从哪里来？它停留多久？它如何消失？你会发现，情绪就像天上的云，来无影去无踪，没有固定的自性。这就是情绪的「空性」。</p>
      <p>并非要你压抑情绪，而是当你能以观照者的角度看待情绪时，你就不会被它所主宰。愤怒来了，你看到它；它停留，你不抗拒；它去了，你不留恋。这便是「色即是空」在情绪层面上的应用。</p>
      <h2>日常三练——简单有效的修行方法</h2>
      <p><strong>一、呼吸觉知：</strong>每天抽出五分钟，安静地坐着，将注意力完全放在呼吸上。感受气息进出鼻孔的感觉，不需要控制呼吸，只是观察。每当心念跑走，轻轻地把它带回来。</p>
      <p><strong>二、行走禅：</strong>走路时把注意力放在脚下的感觉——脚底接触地面的触感、重心转移的过程。不需要走得很慢，只是带着觉知去走每一步。</p>
      <p><strong>三、感恩日记：</strong>每天睡前写下三件让你感恩的事。不一定要是大事，一杯热茶、一个微笑都可以。这能帮助我们超越「自我」的局限视角。</p>
    `,
    image: 'https://readdy.ai/api/search-image?query=Person%20meditating%20peacefully%20in%20a%20minimalist%20modern%20living%20room%2C%20morning%20sunlight%20streaming%20through%20large%20window%2C%20indoor%20plants%2C%20wooden%20floor%2C%20serene%20contemporary%20home%20setting%2C%20warm%20natural%20tones%2C%20mindfulness%20lifestyle%20photography&width=800&height=450&seq=article-practice-03&orientation=landscape',
    date: '2024-11-10',
    author: '释明心',
    readTime: '10分钟',
    tags: ['修行实践', '日常生活', '正念', '空性修行'],
  },
  {
    id: 'fang-xia-zhi-nian',
    title: '如何放下执念——从佛学智慧中找到解脱之道',
    slug: 'fang-xia-zhi-nian',
    category: 'xiu-xing-shi-jian',
    categoryName: '修行实践',
    excerpt: '执念是痛苦之源。本文从佛教缘起性空的智慧出发，探讨执念的成因与放下执念的具体方法，帮助你从内心的纠结中解脱。',
    content: `
      <h2>执念从何而来？</h2>
      <p>执念的本质，是对「自我」和「我所拥有」的牢固认定。我们认为这个身体是「我」，这些财富是「我的」，这段关系是「我的」。当这些被威胁或失去时，痛苦就产生了。</p>
      <p>但佛陀告诉我们：所谓的「我」也不过是五蕴（色受想行识）的暂时聚合，没有一个永恒不变的实体。既然「我」都没有固定本质，那么「我的」又从何谈起？</p>
      <h2>放下不是放弃</h2>
      <p>很多人误以为「放下」就是什么都不要，这是极端的误解。放下是指放下内心的执著和贪恋，而不是放弃外在的事物和责任。</p>
      <p>你可以拥有财富，但不被财富所困；你可以建立关系，但不被关系所绑。正如莲花出淤泥而不染——在世间生活，却不被世间的烦恼所染污。</p>
      <h2>放下执念的四步练习</h2>
      <p><strong>第一步：觉察执念。</strong>当你感到痛苦时，问自己：我在执著什么？是某个结果？是某个人？还是某个身份认同？</p>
      <p><strong>第二步：分析无常。</strong>你所执著的对象是永恒不变的吗？如果不是，那你执著的其实只是一个暂时的幻象。</p>
      <p><strong>第三步：观想空性。</strong>用「色即是空」的智慧来观照：这个让你痛苦的事物，它的本质是空的。它只是因缘和合的暂时现象。</p>
      <p><strong>第四步：慈悲转化。</strong>将注意力从「我失去了什么」转向「我能给予什么」。慈悲心是化解执念最强大的力量。</p>
    `,
    image: 'https://readdy.ai/api/search-image?query=Lotus%20flower%20blooming%20on%20a%20calm%20pond%20surface%2C%20morning%20mist%2C%20soft%20golden%20sunlight%2C%20reflection%20in%20still%20water%2C%20Zen%20garden%20background%2C%20peaceful%20and%20pure%20atmosphere%2C%20spiritual%20symbolism%2C%20fine%20art%20nature%20photography&width=800&height=450&seq=article-letgo-04&orientation=landscape',
    date: '2024-10-20',
    author: '释明心',
    readTime: '8分钟',
    tags: ['放下执念', '修行方法', '解脱', '佛学智慧'],
  },
  {
    id: 'chan-zuo-ming-xiang-ru-men',
    title: '禅坐冥想入门指南——从零开始的静心之旅',
    slug: 'chan-zuo-ming-xiang-ru-men',
    category: 'xiu-xing-shi-jian',
    categoryName: '修行实践',
    excerpt: '冥想并非神秘莫测的宗教仪式，而是人人可学的心灵训练。本文为零基础的你提供一份完整的禅坐冥想入门指南。',
    content: `
      <h2>什么是禅坐冥想？</h2>
      <p>禅坐冥想（Meditation）是一种训练心智的方法，通过将注意力集中在特定对象上（如呼吸、身体感受），来培养觉知力和专注力。它与宗教无关，是一种普适的心灵训练技术。</p>
      <h2>准备工作</h2>
      <p>找一个安静的地方，准备一个舒适的坐垫。你可以盘腿坐在蒲团上，也可以坐在椅子上。关键是保持脊柱自然挺直，既不过分紧张也不松垮。</p>
      <p>穿宽松舒适的衣服，关掉手机通知。初学者建议从5-10分钟开始，逐渐延长到20-30分钟。</p>
      <h2>基本步骤</h2>
      <p><strong>第一步：调身。</strong>找到一个舒适的坐姿，轻轻闭上眼睛，双手自然放在膝盖上或结禅定印。</p>
      <p><strong>第二步：调息。</strong>自然呼吸，不要刻意控制。将注意力轻轻放在鼻孔下方的区域，感受气息进出的微妙感觉。</p>
      <p><strong>第三步：调心。</strong>心念会不断地跑走——这是完全正常的。每次发现心念跑走时，不必自责，只是温柔地把它带回到呼吸上。就像训练一只小狗，需要耐心和重复。</p>
    `,
    image: 'https://readdy.ai/api/search-image?query=Simple%20meditation%20cushion%20on%20wooden%20floor%20in%20a%20minimalist%20room%2C%20morning%20sunlight%20creating%20warm%20shadows%2C%20small%20plant%20nearby%2C%20peaceful%20and%20clean%20aesthetic%2C%20zen%20atmosphere%2C%20warm%20neutral%20tones&width=800&height=450&seq=article-meditation-05&orientation=landscape',
    date: '2024-10-05',
    author: '释明心',
    readTime: '7分钟',
    tags: ['禅坐', '冥想', '入门指南', '正念修行'],
  },
  {
    id: 'kong-xing-zhi-hui-zhi-chang-ya-li',
    title: '空性智慧与职场压力管理——佛学给现代人的解药',
    slug: 'kong-xing-zhi-hui-zhi-chang-ya-li',
    category: 'xian-dai-qi-shi',
    categoryName: '现代启示',
    excerpt: '996的压力、KPI的焦虑、人际关系的紧绷——职场是现代人最大的压力源。两千年前的般若智慧，如何帮助我们应对当下的职场困境？',
    content: `
      <h2>现代职场的「苦」</h2>
      <p>佛陀说「人生是苦」，这份苦在现代职场中表现得淋漓尽致。业绩压力、晋升焦虑、同事关系、裁员恐惧……每一天都有无数的烦恼等待着我们。</p>
      <p>但佛陀没有止于「苦」的发现，他进一步指出了苦的原因和灭苦的方法。这，就是「色即是空」能给我们的启示。</p>
      <h2>用空性智慧看待职场压力</h2>
      <p><strong>压力是空的。</strong>当你感到压力巨大时，停下来观察这份压力：它在哪里？在身体里是一股紧绷感，在心理上是一些不断重复的担忧念头。而这股紧绷感和这些念头，都是在不断变化的——它们没有固定的自性，本质是空的。</p>
      <p><strong>评价是空的。</strong>老板的批评、同事的眼光、年终的评级——这些都是因缘和合的产物。今天的差评可能来自老板今早的心情不好，与你的能力未必有关。认识到这一点，你就不会把外界的评价等同于自我的价值。</p>
    `,
    image: 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20workspace%20with%20warm%20wooden%20desk%2C%20laptop%2C%20small%20bonsai%20tree%2C%20soft%20natural%20light%20from%20window%2C%20calm%20and%20focused%20atmosphere%2C%20work%20life%20balance%20concept%2C%20warm%20neutral%20tones&width=800&height=450&seq=article-work-06&orientation=landscape',
    date: '2024-09-18',
    author: '释明心',
    readTime: '9分钟',
    tags: ['职场压力', '现代生活', '空性智慧', '压力管理'],
  },
  {
    id: 'ji-jian-zhu-yi-yu-fo-xue',
    title: '从佛学看极简主义生活——少即是多的智慧',
    slug: 'ji-jian-zhu-yi-yu-fo-xue',
    category: 'xian-dai-qi-shi',
    categoryName: '现代启示',
    excerpt: '极简主义（Minimalism）风靡全球，少物、少欲、少烦恼的生活理念与佛学的「少欲知足」不谋而合。本文探讨两者之间的深度联结。',
    content: `
      <h2>极简主义的兴起</h2>
      <p>在物质极度丰富的今天，越来越多的人开始反思消费主义的生活方式，转向「少即是多」的极简主义。这与佛陀两千五百年前的教诲——少欲知足——有着惊人的相似之处。</p>
      <h2>「拥有」的幻觉</h2>
      <p>我们为什么想要拥有更多的东西？深层来看，是因为我们相信「拥有」能带给我们安全感和幸福感。但佛陀一针见血地指出：所有拥有都是暂时的。你买了一辆新车，它终究会变旧；你收藏了满柜的衣服，时尚总会过季。</p>
      <p>「色即是空」告诉我们：物质世界的本质是空性的，通过积累物质来寻求永恒的满足是注定失败的。</p>
      <h2>从极简到「心简」</h2>
      <p>真正的极简不只是扔东西，更是内心的简化。当你的内心不再被欲望、比较、焦虑所占据时，自然就获得了自由。这恰恰是修行所要达到的状态——内心清净，不为外物所动。</p>
    `,
    image: 'https://readdy.ai/api/search-image?query=Minimalist%20Japanese%20style%20living%20room%20with%20tatami%20mat%2C%20single%20ikebana%20flower%20arrangement%2C%20empty%20space%2C%20warm%20natural%20light%2C%20wooden%20beams%2C%20serene%20and%20uncluttered%2C%20zen%20aesthetic%2C%20architectural%20photography&width=800&height=450&seq=article-minimal-07&orientation=landscape',
    date: '2024-09-02',
    author: '释明心',
    readTime: '8分钟',
    tags: ['极简主义', '少欲知足', '现代生活', '佛学智慧'],
  },
  {
    id: 'zhong-guan-xue-pai-kong-de-lun-shu',
    title: '中观学派对「空」的论述——龙树菩萨的哲学体系',
    slug: 'zhong-guan-xue-pai-kong-de-lun-shu',
    category: 'kong-xing-zhe-xue',
    categoryName: '空性哲学',
    excerpt: '龙树菩萨创立的中观学派是佛教空性哲学的最高峰。本文系统介绍中观学派的核心思想——缘起性空、八不中道、二谛说。',
    content: `
      <h2>中观学派的创立</h2>
      <p>中观学派（Madhyamaka）由龙树菩萨（约公元2-3世纪）创立，是大乘佛教最重要的哲学流派之一。「中观」意为「中道的观点」——不走极端，不偏执于「有」也不偏执于「空」。</p>
      <h2>八不中道</h2>
      <p>龙树菩萨在《中论》开篇便提出了著名的「八不」：不生不灭、不常不断、不一不异、不来不去。这八组否定的目的不是建立一套新的形而上学体系，而是破除我们对世界的一切固定见解。</p>
      <p>「不生不灭」告诉我们：没有一个事物是真正「产生」的——因为产生需要自性或他性的因，而经过分析，这些都不成立。「不常不断」则破除了常见和断见两种极端。</p>
      <h2>二谛说——世俗谛与胜义谛</h2>
      <p>中观学派的重要创见是建立了「二谛」的框架。世俗谛是指日常经验世界的真理——桌子是桌子，山是山。而胜义谛是指从究竟层面来看，一切法都是空性的。</p>
      <p>二者不矛盾：世俗谛是通向胜义谛的阶梯，胜义谛为世俗谛提供了解脱的可能。</p>
    `,
    image: 'https://readdy.ai/api/search-image?query=Ancient%20Buddhist%20philosophical%20manuscript%20with%20Sanskrit%20text%2C%20aged%20palm%20leaf%20pages%2C%20scholarly%20study%20setting%20with%20warm%20light%2C%20traditional%20Indian%20academic%20atmosphere%2C%20historical%20wisdom%2C%20warm%20golden%20and%20earthy%20tones&width=800&height=450&seq=article-madhyamaka-08&orientation=landscape',
    date: '2024-08-25',
    author: '释明心',
    readTime: '15分钟',
    tags: ['中观学派', '龙树菩萨', '空性哲学', '八不中道'],
  },
  {
    id: 'ke-xue-yu-kong-xing',
    title: '科学与空性——量子物理对佛学智慧的印证',
    slug: 'ke-xue-yu-kong-xing',
    category: 'xian-dai-qi-shi',
    categoryName: '现代启示',
    excerpt: '量子物理的一些发现似乎与佛教空性理论不谋而合。本文以通俗易懂的方式，探讨科学与佛学在「实在的本质」这一问题上的对话。',
    content: `
      <h2>量子世界中的「不确定性」</h2>
      <p>在量子力学中，一个粒子在被观测之前并没有确定的位置或动量——它同时存在于多种可能状态的「叠加态」中。只有当你观测它时，它才「选择」了一个确定的状态。</p>
      <p>这与佛教所说的「诸法无自性」有惊人的相似：物质现象并非如我们直觉认为的那样有固定不变的本质，而是依赖于观测条件而呈现。</p>
      <h2>量子纠缠与缘起</h2>
      <p>量子纠缠现象表明，两个曾经相互作用的粒子，无论相隔多远，对其中一个的测量会瞬间影响另一个的状态。爱因斯坦称这为「鬼魅般的超距作用」。</p>
      <p>这与佛教的「缘起」观念产生了奇妙的共鸣：一切事物都与其他事物相互联系、相互依存，没有绝对独立的个体存在。</p>
      <h2>谨慎的态度</h2>
      <p>虽然量子物理与佛学在某些层面上有有趣的呼应，但我们必须谨慎，不能简单地将两者等同。量子物理是精确的数学理论和可验证的实验科学，而佛学是关于心性解脱的智慧体系。两者在方法和目标上有着本质的不同。</p>
      <p>然而，这种跨领域的思想对话，确实为我们理解「色即是空」提供了一个新的视角。</p>
    `,
    image: 'https://readdy.ai/api/search-image?query=Abstract%20visualization%20of%20quantum%20particles%20and%20waves%2C%20warm%20golden%20light%20particles%20floating%20in%20space%2C%20artistic%20rendering%20of%20interconnectedness%2C%20scientific%20yet%20spiritual%20aesthetic%2C%20cosmic%20dust%20and%20energy%20fields%2C%20warm%20tones&width=800&height=450&seq=article-science-09&orientation=landscape',
    date: '2024-08-10',
    author: '释明心',
    readTime: '11分钟',
    tags: ['科学与佛学', '量子物理', '空性', '缘起'],
  },
  {
    id: 'kong-yu-you-de-bian-zheng-guan-xi',
    title: '空与有的辩证关系——从二元对立到中道圆融',
    slug: 'kong-yu-you-de-bian-zheng-guan-xi',
    category: 'kong-xing-zhe-xue',
    categoryName: '空性哲学',
    excerpt: '「空」与「有」看似对立，实则圆融无碍。本文深入探讨佛教哲学中空与有的辩证关系，带你超越二元对立的思维模式。',
    content: `
      <h2>空与有：一对看似矛盾的概念</h2>
      <p>在日常思维中，我们习惯于二元对立的模式：有就是有，没有就是没有。但佛学的「空」概念挑战了这种思维习惯——「空」不是「没有」，而是指没有固定不变的自性。</p>
      <p>说一个杯子是「空」的，不是说这个杯子不存在，而是说这个杯子没有永恒不变的本质。它由泥土制成，经过工匠的手，在窑中烧制——是无数条件聚合的结果。这些条件变化，它也会碎、会化为尘土。</p>
      <h2>真空妙有</h2>
      <p>大乘佛教有一个重要的概念叫做「真空妙有」。真空是指诸法的空性本质，妙有是指空性并不妨碍现象界的丰富多彩。</p>
      <p>正因为万物本性是空的（真空），所以变化才成为可能，万象才得以呈现（妙有）。如果万物有固定的自性，那么种子就永远是种子，不可能长成大树；孩子永远是孩子，不可能长大成人。</p>
    `,
    image: 'https://readdy.ai/api/search-image?query=Abstract%20yin%20yang%20inspired%20composition%20in%20warm%20earthy%20tones%2C%20flowing%20organic%20shapes%2C%20balance%20of%20light%20and%20shadow%2C%20harmonious%20duality%20concept%2C%20minimalist%20zen%20aesthetic%2C%20textured%20paper%20background%2C%20Eastern%20philosophy%20art&width=800&height=450&seq=article-duality-10&orientation=landscape',
    date: '2024-07-28',
    author: '释明心',
    readTime: '13分钟',
    tags: ['空与有', '中道', '辩证关系', '空性哲学'],
  },
  {
    id: 'wu-yun-jie-kong',
    title: '五蕴皆空——从色蕴到识蕴的深度剖析',
    slug: 'wu-yun-jie-kong',
    category: 'xin-jing-jie-du',
    categoryName: '心经解读',
    excerpt: '《心经》开篇便说「照见五蕴皆空」，五蕴是什么？为什么说它们都是空的？本文逐一剖析五蕴的内涵与空性本质。',
    content: `
      <h2>五蕴概说</h2>
      <p>五蕴（pañca-skandha）是佛教分析「人」的构成的基本框架，包括：色蕴（物质）、受蕴（感受）、想蕴（概念认知）、行蕴（意志行为）、识蕴（了别认识）。</p>
      <p>佛陀通过五蕴的分析告诉我们：所谓「我」并不是一个独立的、不变的实体，而只是这五种要素的暂时聚合。</p>
      <h2>逐一剖析</h2>
      <p><strong>色蕴：</strong>指一切物质现象，包括我们的身体和外部世界。色蕴处于不断的变化之中——细胞每时每刻在新陈代谢，没有一刻是相同的。</p>
      <p><strong>受蕴：</strong>感受有三种——乐受、苦受、不苦不乐受。感受像流水一样，快乐不会永恒，痛苦也不会永恒。</p>
      <p><strong>想蕴：</strong>我们对事物的概念化和标签化。比如看到红色，知道这是「红」；听到声音，认出了是什么。但这些概念本身也是因缘所生。</p>
      <p><strong>行蕴：</strong>意志、意愿、冲动等心理活动。它们驱动着我们的行为，但也是因缘和合的产物。</p>
      <p><strong>识蕴：</strong>了别认识的能力——眼识、耳识、鼻识、舌识、身识、意识。识蕴同样依赖于感官与对象的接触而存在。</p>
    `,
    image: 'https://readdy.ai/api/search-image?query=Five%20elements%20abstract%20composition%2C%20flowing%20water%2C%20flame%2C%20earth%20texture%2C%20air%20mist%2C%20space%20void%2C%20harmonious%20circle%20arrangement%2C%20Buddhist%20symbolism%2C%20warm%20golden%20and%20earthy%20tones%2C%20meditative%20art&width=800&height=450&seq=article-five-skandhas-11&orientation=landscape',
    date: '2024-07-15',
    author: '释明心',
    readTime: '14分钟',
    tags: ['五蕴', '五蕴皆空', '心经', '色蕴'],
  },
  {
    id: 'long-shu-kong-xing-si-xiang',
    title: '龙树菩萨的空性思想——中观哲学入门',
    slug: 'long-shu-kong-xing-si-xiang',
    category: 'kong-xing-zhe-xue',
    categoryName: '空性哲学',
    excerpt: '龙树菩萨被誉为「第二佛陀」，他的空性思想奠定了大乘佛教哲学的基础。本文带你走进这位伟大思想家的智慧世界。',
    content: `
      <h2>龙树菩萨：第二佛陀</h2>
      <p>龙树菩萨（Nāgārjuna）生活于约公元2-3世纪的南印度，被尊为「第二佛陀」和「八宗共祖」。他的著作《中论》是佛教哲学史上最具影响力的作品之一。</p>
      <h2>空性不是什么</h2>
      <p>龙树菩萨反复强调：空性（śūnyatā）不是一个「东西」，不是一种终极实体。如果把空当作一个「有」来执取，那就完全误解了空的含义。他甚至说：如果把空性当作一个见解来执著，那这个人是无可救药的。</p>
      <p>空性是一种「治疗方法」，而不是一种「理论体系」。它的作用是破除我们对一切事物的执著，包括对「空」本身的执著。</p>
      <h2>空性的目的</h2>
      <p>理解空性不是为了建立一套高深的哲学体系，而是为了解脱。当我们真正体证了诸法空性时，执著自然消解，烦恼自然消除——这就是般若波罗蜜多（到彼岸的智慧）的意义。</p>
    `,
    image: 'https://readdy.ai/api/search-image?query=Ancient%20Indian%20Buddhist%20university%20ruins%20Nalanda%20style%2C%20warm%20sunset%20light%20on%20stone%20architecture%2C%20scholarly%20monastic%20atmosphere%2C%20historical%20wisdom%20tradition%2C%20peaceful%20and%20timeless%2C%20warm%20earthy%20tones&width=800&height=450&seq=article-nagarjuna-12&orientation=landscape',
    date: '2024-06-30',
    author: '释明心',
    readTime: '16分钟',
    tags: ['龙树菩萨', '中观', '空性思想', '中论'],
  },
];

export const categories = [
  {
    slug: 'xin-jing-jie-du',
    name: '心经解读',
    description: '深入解读《般若波罗蜜多心经》每一句偈语，探索色即是空的真正含义',
    icon: 'ri-book-open-line',
    count: 3,
  },
  {
    slug: 'xiu-xing-shi-jian',
    name: '修行实践',
    description: '将般若智慧融入日常生活，掌握冥想、正念等实用修行方法',
    icon: 'ri-mental-health-line',
    count: 3,
  },
  {
    slug: 'xian-dai-qi-shi',
    name: '现代启示',
    description: '用佛学空性智慧回应当代社会议题，帮助现代人找到内心平静',
    icon: 'ri-lightbulb-line',
    count: 3,
  },
  {
    slug: 'kong-xing-zhe-xue',
    name: '空性哲学',
    description: '系统学习中观哲学与空性理论，了解龙树菩萨等大师的思想体系',
    icon: 'ri-scales-line',
    count: 3,
  },
];